import { useContext, useCallback, useEffect, useState } from 'react';
import { isAndroid, isIOS, isMobile } from 'react-device-detect';
import useTranslation from 'next-translate/useTranslation';
import { ContestStatus } from '@betterpool/api-types/contests-service';

import { FEATURE } from '~/common/enums/feature.enum';
import { UserContext } from '~/components/providers/UserProvider';
import { useContest } from '~/domains/contest/hooks/useContest';
import EntryNameEditModalContext from '~/domains/contest/domains/gamecenter/domains/common/context/EntryNameEditModalContext/EntryNameEditModalContext';
import { useContestDeepLinkWebToApp } from '~/domains/analytics/branchio/useDeepLink';
import { useTreatments } from '~/domains/common/hooks/useTreatments';
import { useIsTablet } from '~/domains/common/hooks/mediaQueries';
import useGetChannelData from '~/domains/commissioner/channel/channel.hooks';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import { PromptLocation, useAppDownloadPrompt } from '~/hooks/useAppDownloadPrompt';

const enum ContentTab {
  DETAILS = 'details',
  ENTRANTS = 'entrants',
  POSTS = 'posts',
}

const useContestDetail = ({ isSidePanel, hideEntries }: { isSidePanel: boolean, hideEntries: boolean }) => {
  const { data: contestData } = useContest();

  const isTablet = useIsTablet();
  const { t } = useTranslation('contest');
  const isJurisdictionInfoEnabled = useIsFeatureEnabled(
    FEATURE.JURISDICTION_ENABLE_INFO_CONTEST_DETAIL
  );
  const { treatments } = useTreatments([FEATURE.ENABLE_APP_PROMPT]);
  const appPromptConfig = treatments[FEATURE.ENABLE_APP_PROMPT]?.config;
  const handleShowAppDownloadPrompt = useAppDownloadPrompt(PromptLocation.CONTEST_DETAIL, false);

  const showPosts = useIsFeatureEnabled(FEATURE.ENABLE_COMMISSIONER_POSTS);

  const [isAppPromptModalShown, setShowAppPromptModal] = useState(false);
  const tabs = [
    { label: t('detail.tabs.detail'), value: ContentTab.DETAILS },
    ...(!hideEntries ? [{ label: t('detail.tabs.entrants'), value: ContentTab.ENTRANTS }] : []),
    ...(showPosts ? [{ label: t('detail.tabs.posts'), value: ContentTab.POSTS }] : []),
  ];
  const isTabsShown = (isTablet || isSidePanel) && tabs.length > 1;

  const [appUrl] = useContestDeepLinkWebToApp({
    contestId: contestData.id,
  });

  const handleDownloadClick = useCallback(() => {
    if (appPromptConfig.useBranchLink) {
      // <FF> Use Branch link instead of direct Appstore link for mobile devices as well
      window.location.href = appUrl;
      return;
    }

    if (isAndroid) {
      window.location.href =
        'https://play.google.com/store/apps/details?id=com.splash.splashsports';
      return;
    }

    if (isIOS) {
      window.location.href = 'https://apps.apple.com/app/id6451190245';
      return;
    }

    window.location.href = appUrl;
  }, [appUrl, appPromptConfig, isAndroid, isIOS]);

  useEffect(() => {
    setShowAppPromptModal(!!handleShowAppDownloadPrompt());
  }, []);

  const userEntries = contestData.entries.user ?? 0;

  const maxEntriesPerUser = contestData.entries.max_per_user ?? 0;
  const myEntriesStatus =
    contestData.status === ContestStatus.SCHEDULED
      ? `(${userEntries}/${maxEntriesPerUser})`
      : `(${userEntries})`;
  const isAllowedInLocation = contestData.is_allowed_in_location;

  const [activeTab, setActiveTab] = useState<ContentTab>(ContentTab.DETAILS);
  const { user } = useContext(UserContext);

  const { channel } = useGetChannelData(contestData.commissioner.id);
  const { openEntryNameEditModal } = useContext(EntryNameEditModalContext);

  const isNewEntriesAllowed =
    !contestData.is_after_first_late_swap_deadline &&
    contestData.entries.user < contestData.entries.max_per_user &&
    contestData.entries.filled < contestData.entries.max;

  return {
    activeTab,
    channel,
    contestData,
    handleDownloadClick,
    isAllowedInLocation,
    isAppPromptModalShown,
    isJurisdictionInfoEnabled,
    isMobile,
    isNewEntriesAllowed,
    isTabsShown,
    maxEntriesPerUser,
    myEntriesStatus,
    openEntryNameEditModal,
    setActiveTab,
    setShowAppPromptModal,
    tabs,
    user,
  };
};

export default useContestDetail;
export { ContentTab };
